// Gemini Live Service using official @google/genai SDK
// Provides real-time voice conversation with full CRUD function calling

import { GoogleGenAI, Modality } from '@google/genai';
import { Task, CalendarEvent, Project, InboxItem, Habit, Goal } from '../contexts/AppContext';
import { VectorMemoryEntry } from './secondBrainService';
import { UserIdentity, generateIdentityPrompt } from './trainingService';

const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY || '';

export interface DayReflection {
    date: string;
    rating: number;
    wins: string[];
    improvements: string[];
    journal: string;
    gratitude: string[];
}

export interface AppContext {
    projects: Project[];
    tasks: Task[];
    events: CalendarEvent[];
    inboxItems: InboxItem[];
    currentDate: string;
    currentTime: string;
    timezone: string;
    personaPrompt?: string;
    voiceStyle?: string;
    voice?: string; // Gemini voice name (Puck, Charon, Fenrir, etc.)
    recentReflections?: DayReflection[]; // Last few journal/debrief entries
    semanticContext?: VectorMemoryEntry[]; // Top relevant memories from vector search
    // Habits and Goals for context hydration
    habits?: Habit[];
    goals?: Goal[];
    workloadScore?: number; // 0-1 score representing day's scheduled capacity
    // User Identity for personalized context
    userIdentity?: UserIdentity;
}

// Full CRUD function declarations
const tools = [{
    functionDeclarations: [
        // ============ TASKS ============
        {
            name: 'createTask',
            description: 'Create a new task for the user',
            parameters: {
                type: 'OBJECT',
                properties: {
                    title: { type: 'STRING', description: 'Task title - required' },
                    priority: { type: 'STRING', description: 'Priority: high, medium, or normal. Default is medium' },
                    dueDate: { type: 'STRING', description: 'Due date in YYYY-MM-DD format' },
                    timeStart: { type: 'STRING', description: 'Start time like "2:00 PM"' },
                    timeEnd: { type: 'STRING', description: 'End time like "3:00 PM"' },
                    projectName: { type: 'STRING', description: 'Optional project name to assign task to' },
                },
                required: ['title'],
            },
        },
        {
            name: 'listTasks',
            description: 'List the user\'s tasks. Returns current tasks with their IDs.',
            parameters: {
                type: 'OBJECT',
                properties: {
                    showCompleted: { type: 'BOOLEAN', description: 'Include completed tasks. Default false' },
                    projectName: { type: 'STRING', description: 'Filter by project name' },
                },
            },
        },
        {
            name: 'updateTask',
            description: 'Update an existing task by its title',
            parameters: {
                type: 'OBJECT',
                properties: {
                    taskTitle: { type: 'STRING', description: 'Current title of the task to update' },
                    newTitle: { type: 'STRING', description: 'New title for the task' },
                    priority: { type: 'STRING', description: 'New priority: high, medium, or normal' },
                    dueDate: { type: 'STRING', description: 'New due date in YYYY-MM-DD format' },
                    timeStart: { type: 'STRING', description: 'New start time' },
                    timeEnd: { type: 'STRING', description: 'New end time' },
                },
                required: ['taskTitle'],
            },
        },
        {
            name: 'completeTask',
            description: 'Mark a task as complete',
            parameters: {
                type: 'OBJECT',
                properties: {
                    taskTitle: { type: 'STRING', description: 'Title of the task to complete' },
                },
                required: ['taskTitle'],
            },
        },
        {
            name: 'deleteTask',
            description: 'Soft delete a task - moves it to the trash bin. Use this by default when user says "delete".',
            parameters: {
                type: 'OBJECT',
                properties: {
                    taskTitle: { type: 'STRING', description: 'Title of the task to delete. Can say "the task" or "it" to reference the last mentioned task.' },
                },
                required: ['taskTitle'],
            },
        },
        {
            name: 'permanentlyDeleteTask',
            description: 'Permanently delete a task - cannot be recovered. Only use when user explicitly says "permanently delete" or "delete forever".',
            parameters: {
                type: 'OBJECT',
                properties: {
                    taskTitle: { type: 'STRING', description: 'Title of the task to permanently delete' },
                },
                required: ['taskTitle'],
            },
        },
        {
            name: 'restoreTask',
            description: 'Restore a task from the trash bin',
            parameters: {
                type: 'OBJECT',
                properties: {
                    taskTitle: { type: 'STRING', description: 'Title of the task to restore' },
                },
                required: ['taskTitle'],
            },
        },

        // ============ SUBTASKS ============
        {
            name: 'addSubtask',
            description: 'Add a subtask to an existing task',
            parameters: {
                type: 'OBJECT',
                properties: {
                    taskTitle: { type: 'STRING', description: 'Title of the parent task. Can say "the task" or "it" to reference the last mentioned task.' },
                    subtaskTitle: { type: 'STRING', description: 'Title of the subtask to add' },
                },
                required: ['taskTitle', 'subtaskTitle'],
            },
        },
        {
            name: 'completeSubtask',
            description: 'Mark a subtask as complete',
            parameters: {
                type: 'OBJECT',
                properties: {
                    taskTitle: { type: 'STRING', description: 'Title of the parent task' },
                    subtaskTitle: { type: 'STRING', description: 'Title of the subtask to complete' },
                },
                required: ['taskTitle', 'subtaskTitle'],
            },
        },
        {
            name: 'listSubtasks',
            description: 'List all subtasks of a task',
            parameters: {
                type: 'OBJECT',
                properties: {
                    taskTitle: { type: 'STRING', description: 'Title of the task to list subtasks for' },
                },
                required: ['taskTitle'],
            },
        },
        {
            name: 'updateSubtask',
            description: 'Update/rename a subtask',
            parameters: {
                type: 'OBJECT',
                properties: {
                    taskTitle: { type: 'STRING', description: 'Title of the parent task' },
                    subtaskTitle: { type: 'STRING', description: 'Current title of the subtask to update' },
                    newTitle: { type: 'STRING', description: 'New title for the subtask' },
                },
                required: ['taskTitle', 'subtaskTitle', 'newTitle'],
            },
        },
        {
            name: 'deleteSubtask',
            description: 'Delete a subtask from a task',
            parameters: {
                type: 'OBJECT',
                properties: {
                    taskTitle: { type: 'STRING', description: 'Title of the parent task' },
                    subtaskTitle: { type: 'STRING', description: 'Title of the subtask to delete' },
                },
                required: ['taskTitle', 'subtaskTitle'],
            },
        },
        {
            name: 'uncompleteSubtask',
            description: 'Mark a completed subtask as incomplete again',
            parameters: {
                type: 'OBJECT',
                properties: {
                    taskTitle: { type: 'STRING', description: 'Title of the parent task' },
                    subtaskTitle: { type: 'STRING', description: 'Title of the subtask to mark as incomplete' },
                },
                required: ['taskTitle', 'subtaskTitle'],
            },
        },
        {
            name: 'getSessionContext',
            description: 'Get the current session context including recently created items. Use this to refresh your memory of what was created in this conversation.',
            parameters: {
                type: 'OBJECT',
                properties: {},
            },
        },

        // ============ PROJECTS ============
        {
            name: 'createProject',
            description: 'Create a new project',
            parameters: {
                type: 'OBJECT',
                properties: {
                    name: { type: 'STRING', description: 'Project name - required' },
                    icon: { type: 'STRING', description: 'Emoji icon for the project. Default 📁' },
                },
                required: ['name'],
            },
        },
        {
            name: 'listProjects',
            description: 'List all projects with their task counts',
            parameters: {
                type: 'OBJECT',
                properties: {},
            },
        },
        {
            name: 'updateProject',
            description: 'Update an existing project',
            parameters: {
                type: 'OBJECT',
                properties: {
                    projectName: { type: 'STRING', description: 'Current name of the project to update' },
                    newName: { type: 'STRING', description: 'New name for the project' },
                    icon: { type: 'STRING', description: 'New emoji icon' },
                },
                required: ['projectName'],
            },
        },
        {
            name: 'deleteProject',
            description: 'Soft delete (archive) a project. Use this by default. Must specify what to do with tasks in the project.',
            parameters: {
                type: 'OBJECT',
                properties: {
                    projectName: { type: 'STRING', description: 'Name of the project to delete' },
                    handleTasks: { type: 'STRING', description: 'What to do with tasks in project: "delete" to move tasks to trash, "unassign" to keep tasks but remove from project. Ask user if unclear.' },
                },
                required: ['projectName', 'handleTasks'],
            },
        },
        {
            name: 'permanentlyDeleteProject',
            description: 'Permanently delete a project - cannot be recovered. Only use when user explicitly says "permanently delete".',
            parameters: {
                type: 'OBJECT',
                properties: {
                    projectName: { type: 'STRING', description: 'Name of the project to permanently delete' },
                },
                required: ['projectName'],
            },
        },
        {
            name: 'restoreProject',
            description: 'Restore an archived/soft-deleted project from trash',
            parameters: {
                type: 'OBJECT',
                properties: {
                    projectName: { type: 'STRING', description: 'Name of the project to restore' },
                },
                required: ['projectName'],
            },
        },

        // ============ EVENTS ============
        {
            name: 'createEvent',
            description: 'Create a new calendar event',
            parameters: {
                type: 'OBJECT',
                properties: {
                    title: { type: 'STRING', description: 'Event title - required' },
                    date: { type: 'STRING', description: 'Date in YYYY-MM-DD format - required' },
                    timeStart: { type: 'STRING', description: 'Start time like "2:00 PM" - required' },
                    timeEnd: { type: 'STRING', description: 'End time like "3:00 PM" - required' },
                    location: { type: 'STRING', description: 'Optional location' },
                },
                required: ['title', 'date', 'timeStart', 'timeEnd'],
            },
        },
        {
            name: 'listEvents',
            description: 'List upcoming calendar events',
            parameters: {
                type: 'OBJECT',
                properties: {
                    date: { type: 'STRING', description: 'Filter by specific date YYYY-MM-DD' },
                },
            },
        },
        {
            name: 'updateEvent',
            description: 'Update an existing calendar event',
            parameters: {
                type: 'OBJECT',
                properties: {
                    eventTitle: { type: 'STRING', description: 'Current title of the event to update' },
                    newTitle: { type: 'STRING', description: 'New title for the event' },
                    date: { type: 'STRING', description: 'New date in YYYY-MM-DD format' },
                    timeStart: { type: 'STRING', description: 'New start time' },
                    timeEnd: { type: 'STRING', description: 'New end time' },
                    location: { type: 'STRING', description: 'New location' },
                },
                required: ['eventTitle'],
            },
        },
        {
            name: 'deleteEvent',
            description: 'Soft delete a calendar event - moves it to trash bin. Use this by default when user says "delete".',
            parameters: {
                type: 'OBJECT',
                properties: {
                    eventTitle: { type: 'STRING', description: 'Title of the event to delete' },
                },
                required: ['eventTitle'],
            },
        },
        {
            name: 'permanentlyDeleteEvent',
            description: 'Permanently delete a calendar event - cannot be recovered. Only use when user says "permanently delete".',
            parameters: {
                type: 'OBJECT',
                properties: {
                    eventTitle: { type: 'STRING', description: 'Title of the event to permanently delete' },
                },
                required: ['eventTitle'],
            },
        },
        {
            name: 'restoreEvent',
            description: 'Restore a calendar event from the trash bin',
            parameters: {
                type: 'OBJECT',
                properties: {
                    eventTitle: { type: 'STRING', description: 'Title of the event to restore' },
                },
                required: ['eventTitle'],
            },
        },

        // ============ UTILITY ============
        {
            name: 'getCurrentContext',
            description: 'Get the current state of tasks, projects, and events. Use this to refresh your knowledge.',
            parameters: {
                type: 'OBJECT',
                properties: {},
            },
        },

        // ============ NAVIGATION ============
        {
            name: 'startFocusMode',
            description: 'Start focus mode (pomodoro timer) for a specific task. Use when user wants to focus, start a pomodoro, or work on a specific task.',
            parameters: {
                type: 'OBJECT',
                properties: {
                    taskTitle: { type: 'STRING', description: 'Title of the task to focus on. If not provided, will start with first incomplete task.' },
                },
            },
        },
        {
            name: 'navigateTo',
            description: 'Navigate to a specific page in the app. Use when user asks to see analytics, briefing, schedule optimizer, priorities, or other pages.',
            parameters: {
                type: 'OBJECT',
                properties: {
                    page: {
                        type: 'STRING',
                        description: 'The page to navigate to',
                        enum: ['analytics', 'briefing', 'debrief', 'schedule', 'priorities', 'calendar', 'inbox', 'profile'],
                    },
                },
                required: ['page'],
            },
        },
        {
            name: 'generateMorningBriefing',
            description: 'Generate and read aloud the personalized morning briefing. Use when user asks for their briefing or to summarize their day.',
            parameters: {
                type: 'OBJECT',
                properties: {},
            },
        },

        // ============ EVENING DEBRIEF ============
        {
            name: 'startEveningDebrief',
            description: 'Start a guided evening debrief session. Navigate to debrief page and begin walking the user through their day reflection. Use when user says "do my debrief", "evening debrief", "reflect on my day", etc.',
            parameters: {
                type: 'OBJECT',
                properties: {},
            },
        },
        {
            name: 'setDayRating',
            description: 'Set the day rating (1-5 stars) for the current debrief session',
            parameters: {
                type: 'OBJECT',
                properties: {
                    rating: { type: 'NUMBER', description: 'Rating from 1 to 5' },
                },
                required: ['rating'],
            },
        },
        {
            name: 'addDebriefWin',
            description: 'Add a win/accomplishment to the current debrief. Can be called multiple times.',
            parameters: {
                type: 'OBJECT',
                properties: {
                    win: { type: 'STRING', description: 'The win or accomplishment to add' },
                },
                required: ['win'],
            },
        },
        {
            name: 'addDebriefImprovement',
            description: 'Add something the user wants to do differently tomorrow. Can be called multiple times.',
            parameters: {
                type: 'OBJECT',
                properties: {
                    improvement: { type: 'STRING', description: 'What the user will do differently tomorrow' },
                },
                required: ['improvement'],
            },
        },
        {
            name: 'addDebriefGratitude',
            description: 'Add something the user is grateful for. Can be called up to 3 times.',
            parameters: {
                type: 'OBJECT',
                properties: {
                    gratitude: { type: 'STRING', description: 'Something the user is grateful for' },
                },
                required: ['gratitude'],
            },
        },
        {
            name: 'setDebriefJournal',
            description: 'Set the journal entry for the current debrief. This is free-form text capturing the user\'s thoughts.',
            parameters: {
                type: 'OBJECT',
                properties: {
                    journal: { type: 'STRING', description: 'The journal entry text' },
                },
                required: ['journal'],
            },
        },
        {
            name: 'saveEveningDebrief',
            description: 'Save the completed evening debrief. Call this after collecting all responses.',
            parameters: {
                type: 'OBJECT',
                properties: {},
            },
        },

        // ============ REBALANCING ============
        {
            name: 'suggestRebalance',
            description: 'Analyze the day and suggest rebalancing for missed high-priority tasks and incomplete habits. Call when user asks about their day being too full, wants to reorganize, or proactively during morning briefing if workload is high.',
            parameters: {
                type: 'OBJECT',
                properties: {},
            },
        },

        // ============ IDENTITY OBSERVER ============
        {
            name: 'updateUserIdentity',
            description: 'Update the user\'s profile based on observed behavior. Call this when the user demonstrates a new Role (e.g., starts coaching, becomes a parent), a new VIP relationship (e.g., mentions a new partner), or changes their North Star goal. Notify the user naturally when you update their profile.',
            parameters: {
                type: 'OBJECT',
                properties: {
                    addRoles: {
                        type: 'ARRAY',
                        items: { type: 'STRING' },
                        description: 'New roles to add to the user\'s identity (e.g., ["Coach", "Investor"])',
                    },
                    addVipEntities: {
                        type: 'ARRAY',
                        items: { type: 'STRING' },
                        description: 'New VIP entities to protect from auto-rescheduling (e.g., ["Nova", "Wife"])',
                    },
                    updatePrimaryGoal: {
                        type: 'STRING',
                        description: 'New North Star goal if the user\'s focus has shifted',
                    },
                },
            },
        },

        // ============ TASK DECOMPOSITION ============
        {
            name: 'decomposeTask',
            description: 'Breaks a large or vague task into 3-5 actionable sub-tasks organized in a new Project. Call this when the user adds a complex task (e.g., "Write book chapter", "Plan vacation", "Build app") or explicitly asks for help breaking something down.',
            parameters: {
                type: 'OBJECT',
                properties: {
                    newProjectTitle: {
                        type: 'STRING',
                        description: 'Title for the new project (e.g., "Japan Vacation Planning", "Book Chapter Draft")',
                    },
                    subTasks: {
                        type: 'ARRAY',
                        items: {
                            type: 'OBJECT',
                            properties: {
                                title: { type: 'STRING', description: 'Actionable task title' },
                                estimatedDurationMinutes: { type: 'NUMBER', description: 'Estimated time in minutes' },
                                priority: { type: 'STRING', description: 'Priority: high, normal, or low' },
                            },
                        },
                        description: 'Array of 3-5 sub-tasks with title, estimated duration, and priority',
                    },
                },
                required: ['newProjectTitle', 'subTasks'],
            },
        },
    ],
}];

type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';
type AudioCallback = (audioData: ArrayBuffer) => void;
type TextCallback = (text: string) => void;
type FunctionCallCallback = (call: any) => Promise<string>;
type StateCallback = (state: ConnectionState, error?: any) => void;
type ContextProvider = () => AppContext;

export class GeminiLiveSession {
    private ai: GoogleGenAI | null = null;
    private session: any = null;
    private state: ConnectionState = 'disconnected';
    private audioCallbacks: AudioCallback[] = [];
    private textCallbacks: TextCallback[] = [];
    private stateCallbacks: StateCallback[] = [];
    private functionCallCallback: FunctionCallCallback | null = null;
    private contextProvider: ContextProvider | null = null;

    constructor() {
        if (GEMINI_API_KEY && GEMINI_API_KEY !== 'YOUR_GEMINI_API_KEY_HERE') {
            this.ai = new GoogleGenAI({ apiKey: GEMINI_API_KEY });
        }
    }

    onStateChange(callback: StateCallback): void {
        this.stateCallbacks.push(callback);
    }

    private setState(state: ConnectionState, error?: any): void {
        this.state = state;
        this.stateCallbacks.forEach(cb => cb(state, error));
    }

    async connect(initialContext: AppContext, contextProvider: ContextProvider): Promise<void> {
        if (!this.ai) {
            throw new Error('Gemini API key not configured');
        }

        this.contextProvider = contextProvider;
        this.setState('connecting');

        try {
            const systemInstruction = this.buildSystemInstruction(initialContext);

            this.session = await this.ai.live.connect({
                model: 'gemini-2.5-flash-native-audio-preview-12-2025',
                config: {
                    responseModalities: [Modality.AUDIO],
                    speechConfig: {
                        voiceConfig: {
                            prebuiltVoiceConfig: {
                                voiceName: (() => {
                                    const voice = initialContext.voice || 'Puck';
                                    console.log('[Gemini] Using voice:', voice);
                                    return voice;
                                })(),
                            },
                        },
                    },
                    systemInstruction: {
                        parts: [{ text: systemInstruction }],
                    },
                    tools: tools as any,
                },
                callbacks: {
                    onopen: () => {
                        console.log('[Gemini] Connected');
                        this.setState('connected');
                    },
                    onmessage: (message: any) => {
                        this.handleMessage(message);
                    },
                    onerror: (e: any) => {
                        console.error('[Gemini] Error:', e);
                        this.setState('error', e);
                    },
                    onclose: (event: any) => {
                        console.log('[Gemini] Disconnected', event);
                        if (event && event.code === 1006) {
                            console.warn('[Gemini] Abnormal closure (1006)');
                            // Pass the CloseEvent as the error reason regarding the 'error' state
                            this.setState('error', event);
                        } else {
                            this.setState('disconnected');
                        }
                    },
                },
            });

        } catch (error) {
            console.error('[Gemini] Connection failed:', error);
            this.setState('error', error);
            throw error;
        }
    }

    private buildSystemInstruction(ctx: AppContext): string {
        try {
            // Base persona - use custom if provided, otherwise default
            const personaInstruction = ctx.personaPrompt ||
                `You are a helpful productivity assistant for Way2Will.
Be friendly, helpful, and conversational.`;

            const voiceNote = ctx.voiceStyle
                ? `\nSPEAKING STYLE: Be ${ctx.voiceStyle}. Match this tone in all responses.`
                : '';

            // Safely build optional sections
            let tasksSection = 'No tasks';
            try {
                const incompleteTasks = (ctx.tasks || []).filter(t => !t.completed).slice(0, 15);
                if (incompleteTasks.length > 0) {
                    tasksSection = incompleteTasks.map(t =>
                        `- "${t.title || 'Untitled'}" [${t.priority || 'normal'}]${t.dueDate ? ` due ${t.dueDate}` : ''}${t.projectId ? ` (project)` : ''}`
                    ).join('\n');
                }
            } catch (e) {
                console.warn('[Gemini] Error building tasks section:', e);
            }

            let eventsSection = 'No events';
            try {
                const events = (ctx.events || []).slice(0, 10);
                if (events.length > 0) {
                    eventsSection = events.map(e =>
                        `- "${e.title || 'Untitled'}" on ${e.date || 'unknown'} at ${e.timeStart || '?'}-${e.timeEnd || '?'}${e.location ? ` @ ${e.location}` : ''}`
                    ).join('\n');
                }
            } catch (e) {
                console.warn('[Gemini] Error building events section:', e);
            }

            let projectsSection = 'None';
            try {
                if (ctx.projects && ctx.projects.length > 0) {
                    projectsSection = ctx.projects.map(p => `"${p.name || 'Untitled'}" (${p.icon || '📁'})`).join(', ');
                }
            } catch (e) {
                console.warn('[Gemini] Error building projects section:', e);
            }

            // Build the core instruction (always included)
            let instruction = `${personaInstruction}
${voiceNote}

You help users manage tasks, calendar events, and projects through natural voice conversation.

CRITICAL INSTRUCTIONS:
1. When the user asks you to create, update, or delete something, YOU MUST call the appropriate function. Don't just say you'll do it - ACTUALLY CALL THE FUNCTION!
2. After making changes, use getCurrentContext to refresh your knowledge of the current state.
3. Be conversational and confirm what you did briefly after each action.
4. You have PERSISTENT MEMORY during this session - remember all changes you make.
5. When referring to items, use fuzzy matching - the user doesn't need exact titles.
6. Keep responses SHORT and spoken naturally - this is voice, not text!

EVENING DEBRIEF MODE:
When user wants to do their evening debrief (says "let's do my debrief", "evening debrief", "reflect on my day", etc.):
1. Call startEveningDebrief to begin
2. Ask about their day rating (1-5), then call setDayRating
3. Ask about wins/accomplishments, call addDebriefWin for each (can have multiple)
4. Ask what they'd do differently tomorrow, call addDebriefImprovement
5. Ask about 3 things they're grateful for, call addDebriefGratitude for each
6. Ask if they have any journal thoughts, call setDebriefJournal
7. Call saveEveningDebrief to save
Be warm and conversational - you're helping them reflect, not interrogating them. Ask ONE question at a time and wait for their response.

IDENTITY OBSERVER PROTOCOL:
You are responsible for maintaining the user's Identity Profile. Do not wait for settings updates. Actively observe:
1. If user schedules recurring activities (e.g., "Basketball Practice", "Piano lessons"), call updateUserIdentity({ addRoles: ["Coach"] }) or similar.
2. If user consistently prioritizes a person over work, or mentions a family member, add them to VIPs.
3. If user mentions working on a new major goal repeatedly, update their North Star.
4. Notify the user naturally when you update their profile (e.g., "I noticed you're taking on a coaching role - I've added that to your profile so I can better support you.").

FRICTION DETECTIVE PROTOCOL:
Watch for "Boulder Tasks" (tasks > 2 hours or vague outcomes like "build app", "plan vacation", "write thesis", "renovate kitchen").
1. Proactively ask: "That sounds like a big one. Want me to break it down into smaller steps?"
2. If they agree, call decomposeTask with 3-5 actionable sub-tasks organized into a new Project.
3. Use the user's Roles to inform the breakdown (e.g., Developer → technical steps, Marketer → campaign steps, Parent → logistics steps).
4. Never auto-schedule all sub-tasks for today - leave them in the project to be scheduled later.
5. After decomposing, offer to schedule the first task: "Should we schedule the first step for today?"

CURRENT DATE/TIME: ${ctx.currentDate || 'unknown'} ${ctx.currentTime || 'unknown'} (${ctx.timezone || 'unknown'})

PROJECTS: ${projectsSection}

TASKS: 
${tasksSection}

EVENTS:
${eventsSection}`;

            // Safely add optional habit section
            try {
                const habits = (ctx.habits || []).filter(h => !h.deletedAt).slice(0, 8);
                if (habits.length > 0) {
                    const today = ctx.currentDate || '';
                    const habitsText = habits.map(h => {
                        const completedToday = (h.completedDates || []).includes(today);
                        return `- ${h.icon || '✓'} "${h.title || 'Habit'}" [${h.frequency || 'daily'}] - ${completedToday ? '✓ done today' : '○ not done'} | ${h.currentStreak || 0} day streak${(h.longestStreak || 0) > (h.currentStreak || 0) ? ` (best: ${h.longestStreak})` : ''}`;
                    }).join('\n');
                    instruction += `\n\nHABITS (track completion and streaks for motivation):\n${habitsText}`;
                }
            } catch (e) {
                console.warn('[Gemini] Error building habits section:', e);
            }

            // Safely add optional goals section
            try {
                const goals = (ctx.goals || []).filter(g => g.status === 'active' && !g.deletedAt).slice(0, 5);
                if (goals.length > 0) {
                    const goalsText = goals.map(g => {
                        const milestones = g.milestones || [];
                        const completedMilestones = milestones.filter(m => m.completed).length;
                        const totalMilestones = milestones.length;
                        return `- ${g.icon || '🎯'} "${g.title || 'Goal'}" [${g.category || 'other'}] - ${g.progress || 0}% complete${totalMilestones > 0 ? ` (${completedMilestones}/${totalMilestones} milestones)` : ''}${g.targetDate ? ` | target: ${g.targetDate}` : ''}`;
                    }).join('\n');
                    instruction += `\n\nGOALS (connect daily tasks to bigger picture):\n${goalsText}`;
                }
            } catch (e) {
                console.warn('[Gemini] Error building goals section:', e);
            }

            // Safely add workload score
            if (typeof ctx.workloadScore === 'number' && !isNaN(ctx.workloadScore)) {
                const pct = Math.round(ctx.workloadScore * 100);
                let loadNote = '';
                if (ctx.workloadScore > 0.9) loadNote = ' ⚠️ HEAVY DAY';
                else if (ctx.workloadScore > 0.75) loadNote = ' (packed)';
                else if (ctx.workloadScore < 0.3) loadNote = ' (light day)';
                instruction += `\n\nWORKLOAD CAPACITY: ${pct}% scheduled today${loadNote}`;
            }

            // Add user identity context (role, North Star, VIPs)
            if (ctx.userIdentity) {
                const identityPrompt = generateIdentityPrompt(ctx.userIdentity);
                if (identityPrompt) {
                    instruction += identityPrompt;
                }
            }

            // Safely add reflections (skip for now to reduce size - this was optional)
            // Semantic context is also skipped to keep instruction size manageable

            return instruction;

        } catch (error) {
            console.error('[Gemini] buildSystemInstruction failed, using minimal instruction:', error);
            // Return minimal fallback instruction
            return `You are a helpful productivity assistant for Way2Will.
Be friendly, helpful, and conversational.
You help users manage tasks, calendar events, and projects through natural voice conversation.
When users ask you to create, update, or delete something, call the appropriate function.
Keep responses SHORT and spoken naturally - this is voice, not text!`;
        }
    }

    // Get current context for refreshing AI's knowledge
    getCurrentContext(): string {
        if (!this.contextProvider) return 'No context available';
        const ctx = this.contextProvider();
        return `Current state:
PROJECTS: ${ctx.projects.map(p => `"${p.name}"`).join(', ') || 'None'}
TASKS: ${ctx.tasks.filter(t => !t.completed).slice(0, 10).map(t => `"${t.title}" [${t.priority}]`).join(', ') || 'None'}
EVENTS: ${ctx.events.slice(0, 5).map(e => `"${e.title}" on ${e.date}`).join(', ') || 'None'}`;
    }

    private handleMessage(message: any): void {
        console.log('[Gemini] Message:', JSON.stringify(message).slice(0, 500));

        if (message.serverContent?.modelTurn?.parts) {
            for (const part of message.serverContent.modelTurn.parts) {
                if (part.inlineData?.data) {
                    const audioData = this.base64ToArrayBuffer(part.inlineData.data);
                    this.audioCallbacks.forEach(cb => cb(audioData));
                }
                if (part.text) {
                    console.log('[Gemini] Text:', part.text);
                    this.textCallbacks.forEach(cb => cb(part.text));
                }
                if (part.functionCall) {
                    console.log('[Gemini] Function:', part.functionCall.name, part.functionCall.args);
                    this.handleFunctionCall(part.functionCall);
                }
            }
        }

        if (message.toolCall?.functionCalls) {
            for (const call of message.toolCall.functionCalls) {
                console.log('[Gemini] Tool call:', call.name, call.args);
                this.handleFunctionCall(call);
            }
        }
    }

    private async handleFunctionCall(call: any): Promise<void> {
        if (!this.session) return;

        // Handle getCurrentContext internally
        if (call.name === 'getCurrentContext') {
            const result = this.getCurrentContext();
            await this.session.sendToolResponse({
                functionResponses: [{
                    id: call.id || 'ctx_' + Date.now(),
                    name: call.name,
                    response: { result },
                }],
            });
            return;
        }

        if (!this.functionCallCallback) {
            console.warn('[Gemini] No function callback');
            return;
        }

        try {
            const result = await this.functionCallCallback(call);
            console.log('[Gemini] Function result:', result);

            await this.session.sendToolResponse({
                functionResponses: [{
                    id: call.id || 'func_' + Date.now(),
                    name: call.name,
                    response: { result },
                }],
            });
        } catch (error) {
            console.error('[Gemini] Function error:', error);
        }
    }

    sendAudio(audioData: ArrayBuffer): void {
        if (!this.session || this.state !== 'connected') return;
        try {
            const base64 = this.arrayBufferToBase64(audioData);
            this.session.sendRealtimeInput({
                audio: { data: base64, mimeType: 'audio/pcm;rate=16000' },
            });
        } catch (error) {
            console.error('[Gemini] Send error:', error);
        }
    }

    endTurn(): void {
        console.log('[Gemini] Turn ended');
    }

    disconnect(): void {
        if (this.session) {
            this.session.close();
            this.session = null;
        }
        this.setState('disconnected');
    }

    onAudioResponse(cb: AudioCallback): void { this.audioCallbacks.push(cb); }
    onTextResponse(cb: TextCallback): void { this.textCallbacks.push(cb); }
    onFunctionCall(cb: FunctionCallCallback): void { this.functionCallCallback = cb; }
    getState(): ConnectionState { return this.state; }
    isReady(): boolean { return this.state === 'connected' && this.session !== null; }

    private arrayBufferToBase64(buffer: ArrayBuffer): string {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
        return btoa(binary);
    }

    private base64ToArrayBuffer(base64: string): ArrayBuffer {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        return bytes.buffer;
    }
}
